import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { Grid } from "@material-ui/core";

import { debounce } from "lodash";

import { getAdjustments } from "api/adjustment";

import { StatusLabel } from "components/Custom";
import { InputContainer } from "components/Form";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";

import { Check, X } from "icons";

import themes from "themes";

import { initialAPIListValue } from "utils/constants";
import { convertDataToTableView } from "utils/convert";
import { timezoneFormat } from "utils/date";
import { exportToExcel } from "utils/exportToExcel";
import getClearAdornment from "utils/form";
import parseJwt from "utils/jwt";
import { getWithExpiry } from "utils/storage";

import AdjustmentCountsModal from "./AdjustmentCountsModal";
import AdjustmentFilter from "./AdjustmentFilter";

export default function Adjustments() {
  const query = new URLSearchParams(useLocation().search);
  const [adjustmentId, setAdjustmentId] = useState("");
  const headers = [
    {
      id: "product__sku",
      label: "product_sku",
      exportLabel: "Inventory Id",
      render: (r, exported) =>
        exported ? (
          r.product.sku
        ) : (
          <span
            onClick={() => setAdjustmentId(r.id)}
            style={{
              color: themes.default.palette.colors.blue_button,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {r.product.sku}
          </span>
        ),
      sortable: true,
    },
    {
      id: "location__code",
      label: "location",
      render: (r, exported) => {
        if (exported) {
          return r.location.code;
        }
        const isDeleted = r.location.is_deleted === true;
        return (
          <StatusLabel
            status={isDeleted ? "Cancelled" : "Open"}
            text={r.location.code}
            altText={isDeleted ? "Inactive" : ""}
          />
        );
      },
      sortable: true,
    },

    {
      id: "qty",
      exportLabel: "Quantity",
      sortable: true,
      render: (r) => r.qty,
    },
    {
      id: "created_at",
      sortable: true,
      render: (r) => timezoneFormat(r.created_at),
    },
    {
      id: "is_published",
      sortable: true,
      render: (r, exported) =>
        exported ? (
          r.is_published ? (
            "Published"
          ) : (
            "Not Published"
          )
        ) : r.is_published ? (
          <Check color="green" />
        ) : (
          <X color="red" />
        ),
    },
  ];

  const claims = parseJwt(getWithExpiry("access"));
  const { related_product } = claims;

  const { t } = useTranslation();
  const [adjustments, setAdjustments] = useState(initialAPIListValue);
  const [selectedAdjustments, setSelectedAdjustments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [orderDirection, setOrderDirection] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_at");
  const [params, setParams] = useState({
    location__code: "",
    is_published: false,
    product__sku: "",
    product__product_type: related_product || 1,
    start_date: "",
    end_date: "",
    type: query.get("type"),
  });

  useEffect(() => {
    if (adjustments.isFetching) {
      let filters = {
        ...params,
        limit: rowsPerPage,
        offset: rowsPerPage * page,
      };
      if (orderDirection && orderBy) {
        params.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }
      getAdjustments({
        params: filters,
        setFunc: setAdjustments,
        paginated: true,
      });
    }
  }, [
    rowsPerPage,
    page,
    params,
    adjustments.isFetching,
    orderDirection,
    orderBy,
  ]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setAdjustments((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setAdjustments(initialAPIListValue);
  };

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setAdjustments((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeFilterInput = (prop) => ({ target: { value } }) => {
    if (prop === "type") {
      setSelectedAdjustments([]);
    }
    setParams({ ...params, [prop]: value });
    debouncedSearchHandler();
  };

  const clearHandler = (input) => {
    setParams((prev) => ({ ...prev, [input]: "" }));
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, params);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce(() => {
      setPage(0);
      setAdjustments((prev) => ({ ...prev, isFetching: true }));
    }, 500),
    [],
  );

  const afterSuccessPublish = () => {
    setAdjustments((prev) => ({ ...prev, isFetching: true }));
    setSelectedAdjustments([]);
  };

  const exportData = (data) => {
    const today = new Date();
    exportToExcel(
      [],
      `Adjustments-${today.toLocaleDateString()}`,
      true,
      convertDataToTableView(data, [
        {
          id: "id",
        },
        ...headers,
      ]),
    );
  };

  const handleExportData = () => {
    if (adjustments.items.length === adjustments.count) {
      exportData(adjustments.items);
    } else {
      getAdjustments({
        params: {
          limit: adjustments.count,
          ...params,
        },
        setFunc: ({ items: { results } }) => exportData(results),
      });
    }
  };

  return (
    <>
      <PageTitle title={t("adjustments")} helpText="Cycle Count records" />
      <InputContainer>
        <AdjustmentFilter
          clearAdornment={clearAdornment}
          handleChangeFilterInput={handleChangeFilterInput}
          params={params}
          setParams={setParams}
          debouncedSearchHandler={debouncedSearchHandler}
          headers={headers}
          afterSuccessPublish={afterSuccessPublish}
          selectedAdjustments={selectedAdjustments}
        />
      </InputContainer>
      <Grid container spacing={4}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <DefaultTable
            data={adjustments}
            headers={headers}
            rowsPerPage={rowsPerPage}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            sortActions={{ onSort, orderBy, orderDirection }}
            hasCheckBox={true}
            selected={selectedAdjustments}
            setSelected={setSelectedAdjustments}
            handleExportData={handleExportData}
          />
          {adjustmentId && (
            <AdjustmentCountsModal
              adjustmentId={adjustmentId}
              onClose={() => setAdjustmentId("")}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
}
